import React, { useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCPopover, RCCRefSpreadsheetProps, RCCServicesApi, RCCTooltip } from "react-common-components";
import Accordion, { CLASSNAME_ACCORDION_HEADER_SHOW_ONLY_ON_HOVER } from "src/components/Accordion";
import Loader from "src/components/Loader";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import SortableHandle from "src/components/Sortable/Handle";
import Spreadsheet from "src/components/Spreadsheet";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import ReactDOMServer from "react-dom/server";
import { IListOfCriteria } from "../interface";
import { ContainerAccordion, WrapTextWithoutLineClamp } from "../styles";
import ModalAttachments from "./attachments";
import IMarketShareAttachmentsRef from "./attachments/interface";
import ModalComments from "./comments";
import IMarketShareCommentsRef from "./comments/interface";

const Groupers: React.FC<IListOfCriteria> = ({
	grouperData,
	selectedMarketShare,
	refModalGrouper,
	refModalGraphic
}) => {

	const { translate, currentLanguage} = useTranslation();
	const {checkIfHasSpecificPermission, administrator, superUser} = useAuth();
	const { addToast } = RCCHookToast();

	const [expandedAccordion, setExpandedAccordion] = useState(false);
	const [ data, setData] = useState<string[][] | null>(null);
	const [loading, setIsLoading] = useState(true);

	const spreadsheetRef = useRef<RCCRefSpreadsheetProps>(null);
	const modalAttachment = useRef<IMarketShareAttachmentsRef>(null);
	const modalComments = useRef<IMarketShareCommentsRef>(null);

	const loadData = async () => {
		try{
			setIsLoading(true);
			const response = await RCCServicesApi.getRequest({
				method: "MarketShare/SelectGrouperData",
				urlParameters: {
					mab004Id: grouperData.mab004Id,
					mab001Id: selectedMarketShare.mab001Id
				}
			});

			const dataLength = response.data.length - 1;
			const dt = response.data.map((row: any, i: number) => {
				const newRow = row;
				if(newRow[1] !== null && newRow[1] !== ""){
					newRow[12] = i === dataLength ? "" : ReactDOMServer.renderToStaticMarkup(<div className="data-action-jexcel-comment-analysis-marketshare" data-description={newRow[0]} data-id={newRow[1]}><RCCIcon name={ newRow[12] === "0" ?  "comment" : "commentFilled"} color="#aaa" size={16} /></div>);
					newRow[13] = i === dataLength ? "" : ReactDOMServer.renderToStaticMarkup(<div className="data-action-jexcel-attachment-analysis-marketshare" data-description={newRow[0]} data-id={newRow[1]}><RCCIcon name="attachment" color="#aaa" size={16}/> {newRow[13] === "0" ? "" : newRow[13]}</div>);
				}
				return newRow;
			});

			setData(dt);
			setIsLoading(false);	
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os dados"), addToast);
			setIsLoading(false);
		}
	};

	const onSaveSpreadsheet = async () => {
		try{
			const newValuesData: string[][] = spreadsheetRef.current ? spreadsheetRef.current.getData() as string[][] : [];
			newValuesData.pop();
			const newValueAnalysisData = newValuesData.map((line) => line.map((item) => String(item)));

			await RCCServicesApi.putRequest({
				method: "MarketShare/UpdateGrouperData",
				body: {
					mab004Id: grouperData.mab004Id,
					mab004Descricao: grouperData.mab004Descricao,
					mab001Id: grouperData.mab004Mab001Id,
					mab001Titulo: selectedMarketShare.mab001Titulo,
					currentLanguage,
					spreadsheet: {
						data: newValueAnalysisData
					}
				}
			});

			addToast({
				color: "green",
				description: translate("Valores adicionados com sucesso"),
				title: translate("Sucesso")
			});

		}catch(err){
			standardErrorMessages(err, translate("Erro ao adicionar valores"), addToast);
		}
	};


	const handleAccordion = (expanded:boolean) => {
		setExpandedAccordion(expanded);
		if(!expanded)
			return;
		loadData();
	};

	const getYear = (year: string, isMinus?: boolean, minusOrPlus?: number) => {
		const yearUTC = new Date(Date.UTC(parseInt(year,10), 1, 0));
		if(minusOrPlus && minusOrPlus){
			if(minusOrPlus)
				return yearUTC.getFullYear() - minusOrPlus;
			return yearUTC.getFullYear() + minusOrPlus;
		}
		return yearUTC.getFullYear().toString();
	};


	return (
		<>
			<ContainerAccordion>
				<div>
					<Accordion 
						expanded={expandedAccordion}
						onChange={(e, expanded) => {e.stopPropagation();  handleAccordion(expanded); }}
						header={
							<>
								<RCCGrid 
									container
									item
									justifyContent="space-between"
									alignItems="center"
									xs={12}
								>
									<RCCGrid
										item
										md={10}
										xs={12}
									>
										<RCCGrid
											item
											xs={12}
											styleInline="display: flex"
										>
											<WrapTextWithoutLineClamp>
												{grouperData.mab004Descricao}
											</WrapTextWithoutLineClamp>
										
											{grouperData.mab004Explicacao && (
												<RCCPopover
													position="bottom"
													header={{title:translate("Explicação")}}
													body={{text:`${grouperData.mab004Explicacao}`}}
												>
													<RCCIcon
														style={{marginLeft: 5}}
														name="infoCircle"
														color="grey"
													/>
												</RCCPopover>
											)}
										</RCCGrid>
									</RCCGrid>
									<RCCGrid
										container 
										item
										justifyContent="flex-end"
										md={2}
										xs={12}
										className={CLASSNAME_ACCORDION_HEADER_SHOW_ONLY_ON_HOVER}
									>
										<RCCTooltip
											text={`${translate("Gráfico")} ${selectedMarketShare.mab001Labelagrupador}`}
											color="blue"
										>
											<RCCButton
												icon={{name: "chartBar", size: 12}}
												outlined
												color="blue"
												size="xs"
												layout="isRounded"
												onClick={(e) => {e.stopPropagation();  refModalGraphic.current?.openModalGraphic(grouperData.mab004Id, new Date(selectedMarketShare.mab001Ano).getFullYear() + 1, grouperData.mab004Descricao, selectedMarketShare.mab001Labelagrupador); }}
											/>
										</RCCTooltip>
										{(administrator || superUser || selectedMarketShare.permission >= 2) && (
											<>
												<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
													<RCCTooltip
														text={`${translate("Editar")} ${selectedMarketShare.mab001Labelagrupador}`}
														color="grey"
													>
														<RCCButton
															icon={{name: "pencil", size: 12}}
															outlined
															color="grey"
															size="xs"
															layout="isRounded"
															onClick={(e) => {e.stopPropagation();  refModalGrouper.current?.actionButtonChange(grouperData.mab004Id, selectedMarketShare.mab001Labelagrupador); }}
														/>
													</RCCTooltip>
												</CheckPermission>
												<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
													<RCCTooltip
														text={`${translate("Excluir")} ${selectedMarketShare.mab001Labelagrupador}`}
														color="red"
													>
														<RCCButton
															icon={{name: "trash", size: 12}}
															outlined
															color="red"
															size="xs"
															layout="isRounded"
															onClick={(e) => {e.stopPropagation(); refModalGrouper.current?.actionButtonDelete(grouperData, selectedMarketShare.mab001Labelagrupador);}}
														/>
													</RCCTooltip>	
												</CheckPermission>
												<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
													<SortableHandle>
														<RCCTooltip 
															color="grey"
															text={`${translate("Mover")} ${selectedMarketShare.mab001Labelagrupador}`}
														>
															<RCCButton
																color="grey"
																outlined
																size="xs"
																icon={{
																	name: "arrows"
																}}
																layout="isRounded"
																onClick={(e) => e.stopPropagation()}
															/>
														</RCCTooltip>	
													</SortableHandle>
												</CheckPermission>
											</>
										)}
									</RCCGrid>
								</RCCGrid>
							</>}
					>
						{loading ? (
							<Loader />
						) : (
							<>
								{data && data.length > 0 && (
									<Spreadsheet 
										ref={spreadsheetRef}
										data={data}
										editable={(administrator || superUser || selectedMarketShare.permission >= 2) && checkIfHasSpecificPermission("analysis/marketshare", PermissionEnum.CREATE_UPDATE)}
										columns={[
											{ type: "text", title: translate("Concorrentes"), width: 200, readOnly: true, wordWrap: true },
											{ type: "hidden", title: "", width: 0 },
											{ type: "numeric", title: `${translate("Faturamento")} ${getYear(selectedMarketShare.mab001Ano,true, 2)}`, width: 120,  decimal: "," },
											{ type: "numeric", title: `Market Share ${getYear(selectedMarketShare.mab001Ano,true, 2)} (%)`, width: 120, readOnly: true,  decimal: "," },
											{ type: "numeric", title: `${translate("Faturamento")} ${new Date(selectedMarketShare.mab001Ano).getFullYear()}`, width: 120,  decimal: "," },
											{ type: "numeric", title: `${translate("Crescimento")} ${getYear(selectedMarketShare.mab001Ano, true, 1)}/${getYear(selectedMarketShare.mab001Ano,true, 2)}`, width: 120, readOnly: true, decimal: "," },
											{ type: "numeric", title: "%", width: 60, readOnly: true,  decimal: "," },
											{ type: "numeric", title: `Market Share ${getYear(selectedMarketShare.mab001Ano, true, 1)} (%)`, width: 120,readOnly: true,  decimal: "," },
											{ type: "numeric", title: `${translate("Faturamento")} ${getYear(selectedMarketShare.mab001Ano)}`, width: 120,  decimal: "," },
											{ type: "numeric", title: `${translate("Crescimento")} ${getYear(selectedMarketShare.mab001Ano)}/${getYear(selectedMarketShare.mab001Ano, true, 1)}`, width: 120,readOnly: true,  decimal: "," },
											{ type: "numeric", title: "%", width: 60,readOnly: true,  decimal: "," },
											{ type: "numeric", title: `Market Share ${getYear(selectedMarketShare.mab001Ano)} (%)`, width: 120, readOnly: true,  decimal: "," },
											{ type: "html", title: " ", width: 50, readOnly: true },
											{ type: "html", title: " ", width: 50, readOnly: true }
										]}
										readonlyLastRows={1}
										format={{
											columns: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
										}}
										showButtons={(administrator || superUser || selectedMarketShare.permission >= 2) && checkIfHasSpecificPermission("analysis/marketshare", PermissionEnum.CREATE_UPDATE)}
										actionSaveButton={onSaveSpreadsheet}
										onMount={() => {
											if(spreadsheetRef && spreadsheetRef.current){
												const refComments = spreadsheetRef.current?.HTMLElement?.querySelectorAll("div.data-action-jexcel-comment-analysis-marketshare");
												const refAttachments = spreadsheetRef.current?.HTMLElement?.querySelectorAll("div.data-action-jexcel-attachment-analysis-marketshare");
												refComments?.forEach((ab: any) => {
													const idCompetitor = ab.getAttribute("data-id"); 
													const descriptionCompetitor = ab.getAttribute("data-description");
													ab.addEventListener("click", () => {
														modalComments.current?.openModalComments(idCompetitor, grouperData.mab004Id, descriptionCompetitor,(administrator || superUser || selectedMarketShare.permission >= 2) && checkIfHasSpecificPermission("analysis/marketshare", PermissionEnum.CREATE_UPDATE), loadData);
													});
												});
												refAttachments?.forEach((ab: any) => {
													const idCompetitor = ab.getAttribute("data-id"); 
													const descriptionCompetitor = ab.getAttribute("data-description");
													ab.addEventListener("click", () => {
														modalAttachment.current?.openModalAttachments(idCompetitor, grouperData.mab004Id, descriptionCompetitor, (administrator || superUser || selectedMarketShare.permission >= 2) && checkIfHasSpecificPermission("analysis/marketshare", PermissionEnum.CREATE_UPDATE), loadData);
													});
												});
											}
										}}
									/>
								)}
							
							</>
						)}	
					</Accordion>
				</div>
			</ContainerAccordion>

			<ModalAttachments 
				ref={modalAttachment}
			/>

			<ModalComments
				ref={modalComments}
			/>

		</>
	);
};


export default Groupers;
